import React, { useState, useEffect, useCallback } from "react";

// ── Types ────────────────────────────────────────────────────────────
type ShiftValue = "" | "DAY" | "NIGHT" | "DOUBLE SHIFT" | "OFF" | "LEAVE";

type Employee = {
  employeeId: string;
  employeeName: string;
};

type RosterGrid = Record<string, Record<number, ShiftValue>>;

// Staff list is the same every time, so fetch it once per session and reuse
// it. Keyed by branch name. Survives leaving and re-opening the Roster tab;
// clears on a full page reload.
const rosterStaffCache: Record<string, Employee[]> = {};

// ── Branch shift-time metadata ───────────────────────────────────────
const BRANCH_META: Record<
  string,
  { name: string; shifts: { label: string; time: string }[] }
> = {
  GRD: {
    name: "Gardens",
    shifts: [
      { label: "Day", time: "09:00 – 16:30" },
      { label: "Night", time: "15:30 – 00:30" },
    ],
  },
  PLM: {
    name: "Plumstead",
    shifts: [
      { label: "Day", time: "09:00 – 16:30" },
      { label: "Night", time: "15:30 – 00:30" },
    ],
  },
  PRK: {
    name: "Parklands",
    shifts: [
      { label: "Day", time: "10:30 – 17:30" },
      { label: "Night", time: "16:00 – 00:00" },
      { label: "Double", time: "11:00 – 00:00" },
    ],
  },
  SEA: {
    name: "Sea Point",
    shifts: [
      { label: "Day", time: "09:00 – 16:30" },
      { label: "Night", time: "15:30 – 00:30" },
    ],
  },
};

const SHIFT_OPTIONS: ShiftValue[] = ["", "DAY", "NIGHT", "DOUBLE SHIFT", "OFF", "LEAVE"];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ── Helpers ──────────────────────────────────────────────────────────
function getMonday(d: Date): Date {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = date.getDay();
  date.setDate(date.getDate() - ((dow + 6) % 7));
  return date;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function fmtShort(d: Date): string {
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function fmtISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getAuthToken(): string | null {
  try {
    const raw = localStorage.getItem("gotrue.user");
    if (!raw) return null;
    return JSON.parse(raw)?.token?.access_token ?? null;
  } catch {
    return null;
  }
}

function getUserBranch(): string | null {
  try {
    const raw = localStorage.getItem("gotrue.user");
    if (!raw) return null;
    const user = JSON.parse(raw);
    const token: string | undefined = user?.token?.access_token;
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    const roles: string[] = payload?.app_metadata?.roles ?? [];
    for (const r of roles) {
      const m = r.match(/^branch([A-Za-z]{3})$/);
      if (m) return m[1].toUpperCase();
    }
    return null;
  } catch {
    return null;
  }
}

// ── Subtle cell styling per value ────────────────────────────────────
function cellStyle(v: ShiftValue): React.CSSProperties {
  switch (v) {
    case "DAY":
      return { color: "var(--ct-ink)", fontWeight: 500 };
    case "NIGHT":
      return { color: "var(--ct-ink-2)", fontWeight: 500 };
    case "DOUBLE SHIFT":
      return { color: "var(--ct-accent)", fontWeight: 600 };
    case "OFF":
      return { color: "var(--ct-ink-3)", fontWeight: 400 };
    case "LEAVE":
      return { color: "#b45309", fontWeight: 500, fontStyle: "italic" };
    default:
      return { color: "var(--ct-ink-3)" };
  }
}

// ── PDF export ───────────────────────────────────────────────────────
function downloadRosterPDF(
  branchName: string,
  shiftInfo: string,
  weekLabel: string,
  employees: Employee[],
  grid: RosterGrid,
  weekStart: Date
) {
  const rows = employees.map((emp) => {
    const row = grid[emp.employeeId] || {};
    const cells = DAY_LABELS.map((_, i) => {
      const val = row[i] || "";
      return `<td style="border:1px solid #ddd;padding:6px 8px;text-align:center;font-size:11px;">${val || "—"}</td>`;
    }).join("");
    return `<tr><td style="border:1px solid #ddd;padding:6px 10px;font-weight:500;font-size:11px;white-space:nowrap;">${emp.employeeName}</td>${cells}</tr>`;
  });

  const dayHeaders = DAY_LABELS.map((d, i) => {
    const date = addDays(weekStart, i);
    return `<th style="border:1px solid #ddd;padding:6px 8px;text-align:center;font-size:10px;background:#f8f9fa;">${d}<br/><span style="font-weight:400;font-size:9px;">${date.getDate()}</span></th>`;
  }).join("");

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>${branchName} Roster — ${weekLabel}</title>
      <style>
        @page { size: landscape; margin: 12mm; }
        body { font-family: -apple-system, "Segoe UI", sans-serif; margin: 0; padding: 20px; }
        h1 { font-size: 16px; margin: 0 0 4px; }
        .sub { font-size: 11px; color: #666; margin-bottom: 16px; }
        table { border-collapse: collapse; width: 100%; }
        th { background: #f1f5f9; }
      </style>
    </head>
    <body>
      <h1>${branchName} — Weekly Roster</h1>
      <div class="sub">${weekLabel} · ${shiftInfo}</div>
      <table>
        <thead>
          <tr>
            <th style="border:1px solid #ddd;padding:6px 10px;text-align:left;font-size:10px;background:#f8f9fa;">Employee</th>
            ${dayHeaders}
          </tr>
        </thead>
        <tbody>${rows.join("")}</tbody>
      </table>
    </body>
    </html>
  `;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (win) {
    win.addEventListener("load", () => {
      setTimeout(() => {
        win.print();
        URL.revokeObjectURL(url);
      }, 300);
    });
  }
}

// ── Component ────────────────────────────────────────────────────────
function RosterEditor({ branchId }: { branchId: string }) {
  const meta = BRANCH_META[branchId] || BRANCH_META.GRD;

  const [weekStart, setWeekStart] = useState<Date>(() => addDays(getMonday(new Date()), 7)); // default to NEXT week (managers plan ahead)
  const [employees, setEmployees] = useState<Employee[]>(
    () => rosterStaffCache[meta.name] || []
  );
  const [grid, setGrid] = useState<RosterGrid>({});
  const [loading, setLoading] = useState(!rosterStaffCache[meta.name]);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [published, setPublished] = useState(false);

  const weekEnd = addDays(weekStart, 6);
  const weekLabel = `${fmtShort(weekStart)} – ${fmtShort(weekEnd)}`;
  const shiftInfo = meta.shifts.map((s) => `${s.label} ${s.time}`).join("  ·  ");

  // Reads from the Staff Roster sheet via get-roster-staff — but only once
  // per session. Later opens of this tab reuse the cached list instantly.
  const loadEmployees = useCallback(async () => {
    // Already fetched this branch's staff earlier — reuse, no server call.
    const cached = rosterStaffCache[meta.name];
    if (cached) {
      setEmployees(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const token = getAuthToken();
      const res = await fetch(
        `/.netlify/functions/get-roster-staff?branch=${encodeURIComponent(meta.name.toLowerCase())}`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      if (!res.ok) throw new Error("Could not load employees.");
      const data = await res.json();
      const emps: Employee[] = (data.staff || []).map((s: any) => ({
        employeeId: s.staffId,
        employeeName: s.name,
      }));
      emps.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
      rosterStaffCache[meta.name] = emps; // store once for the rest of the session
      setEmployees(emps);
    } catch (err) {
      console.error("ROSTER_LOAD:", err);
    } finally {
      setLoading(false);
    }
  }, [meta.name]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  // Persist the manager's working draft on this browser, per branch + week, so
  // reopening the tab brings their roster back instead of a blank grid.
  const persistDraft = (g: RosterGrid) => {
    try {
      localStorage.setItem(`roster_draft_${meta.name}_${fmtISO(weekStart)}`, JSON.stringify(g));
    } catch {}
  };

  // On branch/week change: restore that saved draft (or start blank) and reset
  // the submit state.
  useEffect(() => {
    setPublished(false);
    setSaveMsg(null);
    try {
      const raw = localStorage.getItem(`roster_draft_${meta.name}_${fmtISO(weekStart)}`);
      setGrid(raw ? JSON.parse(raw) : {});
    } catch {
      setGrid({});
    }
  }, [weekStart, meta.name]);

  const setCell = (empId: string, dayIdx: number, value: ShiftValue) => {
    setGrid((prev) => {
      const next = { ...prev, [empId]: { ...(prev[empId] || {}), [dayIdx]: value } };
      persistDraft(next);
      return next;
    });
    setSaveMsg(null);
    setPublished(false);
  };

  const fillRow = (empId: string, value: ShiftValue) => {
    const row: Record<number, ShiftValue> = {};
    for (let i = 0; i < 7; i++) row[i] = value;
    setGrid((prev) => {
      const next = { ...prev, [empId]: row };
      persistDraft(next);
      return next;
    });
    setSaveMsg(null);
    setPublished(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      await new Promise((r) => setTimeout(r, 300));
      setSaveMsg("Draft kept locally. Only 'Publish roster' writes to the sheet.");
    } catch {
      setSaveMsg("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!window.confirm(
      `Submit the ${meta.name} roster for ${weekLabel} for approval?`
    )) return;

    setPublishing(true);
    setSaveMsg(null);
    try {
      const dates = DAY_LABELS.map((_, i) => fmtISO(addDays(weekStart, i)));
      const rosterPayload = employees.map((emp) => ({
        staffId: emp.employeeId,
        name: emp.employeeName,
        shifts: Array.from({ length: 7 }, (_, i) => (grid[emp.employeeId] && grid[emp.employeeId][i]) || ""),
      }));

      const res = await fetch("/.netlify/functions/publish-roster", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ branch: meta.name, dates, roster: rosterPayload }),
      });
      const data = await res.json();

      if (!res.ok || data.ok === false || data.success === false) {
        setSaveMsg(`Could not publish: ${data.error || data.message || res.status}`);
      } else {
        setPublished(true);
        setSaveMsg(`✓ Submitted for approval — an approval ticket was raised for ${weekLabel}.`);
      }
    } catch {
      setSaveMsg("Could not publish. Try again.");
    } finally {
      setPublishing(false);
    }
  };

  const handleDownloadPDF = () => {
    downloadRosterPDF(meta.name, shiftInfo, weekLabel, employees, grid, weekStart);
  };

  const filledCount = Object.values(grid).reduce(
    (sum, row) => sum + Object.values(row).filter((v) => v !== "").length,
    0
  );
  const totalCells = employees.length * 7;
  const allFilled = totalCells > 0 && filledCount === totalCells;

  return (
    <div style={{ padding: "20px 24px 40px" }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: 650,
            letterSpacing: "-0.01em",
            color: "var(--ct-ink)",
            marginBottom: 4,
          }}
        >
          Store Roster
        </div>
        <div style={{ fontSize: 12.5, color: "var(--ct-ink-3)", lineHeight: 1.6 }}>
          <span style={{ fontWeight: 600, color: "var(--ct-ink-2)" }}>{meta.name}</span>
          {"  ·  "}
          {meta.shifts.map((s, i) => (
            <span key={s.label}>
              {i > 0 && "  ·  "}
              {s.label} {s.time}
            </span>
          ))}
        </div>
      </div>

      {/* Week picker */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <button
          onClick={() => setWeekStart(addDays(weekStart, -7))}
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            border: "1px solid var(--ct-line)",
            background: "var(--ct-surface)",
            cursor: "pointer",
            fontSize: 16,
            color: "var(--ct-ink-2)",
            display: "grid",
            placeItems: "center",
            fontFamily: "inherit",
          }}
        >
          ‹
        </button>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: "var(--ct-ink)",
            minWidth: 180,
            textAlign: "center",
          }}
        >
          {weekLabel}
        </div>
        <button
          onClick={() => setWeekStart(addDays(weekStart, 7))}
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            border: "1px solid var(--ct-line)",
            background: "var(--ct-surface)",
            cursor: "pointer",
            fontSize: 16,
            color: "var(--ct-ink-2)",
            display: "grid",
            placeItems: "center",
            fontFamily: "inherit",
          }}
        >
          ›
        </button>
        <button
          onClick={() => setWeekStart(addDays(getMonday(new Date()), 7))}
          style={{
            padding: "6px 12px",
            borderRadius: 8,
            border: "1px solid var(--ct-line)",
            background: "var(--ct-surface)",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 500,
            color: "var(--ct-ink-2)",
            fontFamily: "inherit",
          }}
        >
          Next week
        </button>
      </div>

      {loading && (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--ct-ink-3)", fontSize: 13 }}>
          Loading employees…
        </div>
      )}

      {!loading && employees.length === 0 && (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--ct-ink-3)", fontSize: 13 }}>
          No employees found for this branch.
        </div>
      )}

      {!loading && employees.length > 0 && (
        <>
          {/* Roster grid */}
          <div
            style={{
              border: "1px solid var(--ct-line)",
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                fontFamily: "inherit",
              }}
            >
              <thead>
                <tr style={{ background: "var(--ct-surface)" }}>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "10px 14px",
                      fontWeight: 600,
                      color: "var(--ct-ink-2)",
                      borderBottom: "1px solid var(--ct-line)",
                      width: 180,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Employee
                  </th>
                  {DAY_LABELS.map((d, i) => {
                    const date = addDays(weekStart, i);
                    return (
                      <th
                        key={d}
                        style={{
                          textAlign: "center",
                          padding: "10px 6px",
                          fontWeight: 600,
                          color: "var(--ct-ink-2)",
                          borderBottom: "1px solid var(--ct-line)",
                          minWidth: 100,
                        }}
                      >
                        <div>{d}</div>
                        <div style={{ fontWeight: 400, fontSize: 11, color: "var(--ct-ink-3)" }}>
                          {date.getDate()}
                        </div>
                      </th>
                    );
                  })}
                  <th
                    style={{
                      width: 40,
                      borderBottom: "1px solid var(--ct-line)",
                      padding: "10px 6px",
                    }}
                  />
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, rowIdx) => {
                  const row = grid[emp.employeeId] || {};
                  return (
                    <tr
                      key={emp.employeeId}
                      style={{
                        background: rowIdx % 2 === 0 ? "transparent" : "var(--ct-surface)",
                        transition: "background .08s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "var(--ct-accent-bg)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          rowIdx % 2 === 0 ? "transparent" : "var(--ct-surface)";
                      }}
                    >
                      <td
                        style={{
                          padding: "10px 14px",
                          borderBottom: "1px solid var(--ct-line-soft, var(--ct-line))",
                          whiteSpace: "nowrap",
                          fontWeight: 500,
                          color: "var(--ct-ink)",
                        }}
                      >
                        {emp.employeeName}
                      </td>
                      {DAY_LABELS.map((_, dayIdx) => {
                        const val = row[dayIdx] || "";
                        return (
                          <td
                            key={dayIdx}
                            style={{
                              padding: "6px 4px",
                              textAlign: "center",
                              borderBottom: "1px solid var(--ct-line-soft, var(--ct-line))",
                            }}
                          >
                            <select
                              value={val}
                              onChange={(e) =>
                                setCell(emp.employeeId, dayIdx, e.target.value as ShiftValue)
                              }
                              style={{
                                width: "100%",
                                padding: "6px 4px",
                                borderRadius: 6,
                                border: "1px solid var(--ct-line)",
                                background: "var(--ct-surface)",
                                fontFamily: "inherit",
                                fontSize: 12,
                                cursor: "pointer",
                                outline: "none",
                                appearance: "none",
                                WebkitAppearance: "none",
                                textAlign: "center",
                                ...cellStyle(val),
                              }}
                            >
                              <option value="">—</option>
                              <option value="DAY">DAY</option>
                              <option value="NIGHT">NIGHT</option>
                              <option value="DOUBLE SHIFT">DOUBLE</option>
                              <option value="OFF">OFF</option>
                              <option value="LEAVE">LEAVE</option>
                            </select>
                          </td>
                        );
                      })}
                      <td
                        style={{
                          padding: "6px 6px",
                          borderBottom: "1px solid var(--ct-line-soft, var(--ct-line))",
                        }}
                      >
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value) fillRow(emp.employeeId, e.target.value as ShiftValue);
                          }}
                          title="Fill entire row"
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            border: "1px solid var(--ct-line)",
                            background: "var(--ct-surface)",
                            fontFamily: "inherit",
                            fontSize: 10,
                            cursor: "pointer",
                            color: "var(--ct-ink-3)",
                            outline: "none",
                            padding: 0,
                            textAlign: "center",
                          }}
                        >
                          <option value="">⋮</option>
                          <option value="DAY">All DAY</option>
                          <option value="NIGHT">All NIGHT</option>
                          <option value="DOUBLE SHIFT">All DOUBLE</option>
                          <option value="OFF">All OFF</option>
                          <option value="LEAVE">All LEAVE</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 16,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div style={{ fontSize: 12, color: "var(--ct-ink-3)" }}>
              {filledCount} / {totalCells} cells filled
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              {saveMsg && (
                <span style={{ fontSize: 12, color: "var(--ct-ink-3)" }}>{saveMsg}</span>
              )}

              {/* Download PDF */}
              <button
                onClick={handleDownloadPDF}
                disabled={filledCount === 0}
                style={{
                  padding: "9px 16px",
                  borderRadius: 8,
                  border: "1px solid var(--ct-line)",
                  background: "var(--ct-surface)",
                  color: filledCount > 0 ? "var(--ct-ink-2)" : "var(--ct-ink-3)",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: filledCount > 0 ? "pointer" : "not-allowed",
                }}
              >
                Download PDF
              </button>

              {/* Save draft */}
              <button
                onClick={handleSave}
                disabled={saving || filledCount === 0}
                style={{
                  padding: "9px 16px",
                  borderRadius: 8,
                  border: "1px solid var(--ct-line)",
                  background: "var(--ct-surface)",
                  color: filledCount > 0 ? "var(--ct-ink)" : "var(--ct-ink-3)",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: filledCount > 0 ? "pointer" : "not-allowed",
                }}
              >
                {saving ? "Saving…" : "Save draft"}
              </button>

              {/* Publish roster */}
              <button
                onClick={handlePublish}
                disabled={publishing || !allFilled || published}
                title={
                  !allFilled
                    ? "Fill every cell before submitting"
                    : published
                    ? "Already submitted"
                    : "Submit for the operator to approve"
                }
                style={{
                  padding: "9px 20px",
                  borderRadius: 8,
                  border: "none",
                  background:
                    allFilled && !published
                      ? "var(--ct-accent)"
                      : "var(--ct-surface-3, #e2e8f0)",
                  color: allFilled && !published ? "#fff" : "var(--ct-ink-3)",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: allFilled && !published ? "pointer" : "not-allowed",
                }}
              >
                {publishing ? "Submitting…" : published ? "Submitted ✓" : "Publish roster"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


// ── Operator monitor: read-only view of every branch's roster ─────────
const MONITOR_BRANCHES = ["Gardens", "Plumstead", "Parklands", "Sea Point"];
const WEEKDAY_FULL = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

type MonitorRow = { name: string; staffId: string; days: Record<string, string> };

function RosterMonitor() {
  const [branch, setBranch] = useState<string>("Gardens");
  const [week, setWeek] = useState<"this" | "next">("this");
  const [rows, setRows] = useState<MonitorRow[]>([]);
  const [dayDates, setDayDates] = useState<Record<string, string>>({});
  const [statusLabel, setStatusLabel] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async (b: string, w: "this" | "next") => {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch(
        `/.netlify/functions/get-roster-week?branch=${encodeURIComponent(b)}&week=${w}`
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.status);
      setRows(data.rows || []);
      setDayDates(data.dayDates || {});
      setStatusLabel(data.status || "");
    } catch (e) {
      setErr("Could not load this branch's roster.");
      setRows([]);
      setDayDates({});
      setStatusLabel("");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(branch, week); }, [branch, week, load]);

  const mon = dayDates["Monday"];
  const sun = dayDates["Sunday"];
  const weekLabel = mon && sun ? `${mon} – ${sun}` : (week === "next" ? "Next week" : "Current week");

  // Status comes straight from the sheet: Live (this week's running roster),
  // Approved, Pending review, or Not submitted (next week before it's sent).
  const statusText = rows.length === 0
    ? (week === "next" ? "Not submitted" : "No roster")
    : (statusLabel || "—");
  const statusStyle =
    statusText === "Live" || statusText === "Approved"
      ? { bg: "var(--ct-ok-bg)", fg: "var(--ct-ok)" }
      : statusText === "Pending review"
      ? { bg: "#fef3c7", fg: "#b45309" }
      : { bg: "var(--ct-surface-3, #eef1f5)", fg: "var(--ct-ink-3)" };

  const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: "7px 16px",
    borderRadius: 8,
    border: `1px solid ${active ? "var(--ct-accent)" : "var(--ct-line)"}`,
    background: active ? "var(--ct-accent-bg)" : "transparent",
    color: active ? "var(--ct-accent)" : "var(--ct-ink-2)",
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: active ? 600 : 500,
    cursor: "pointer",
  });

  return (
    <div style={{ padding: "20px 24px 40px" }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 650, letterSpacing: "-0.01em", color: "var(--ct-ink)" }}>
          Store Roster — all branches
        </div>
        <div style={{ fontSize: 12.5, color: "var(--ct-ink-3)", marginTop: 4 }}>
          {week === "next" ? "Next week" : "This week"} · {weekLabel}
        </div>
      </div>

      {/* Branch switcher */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {MONITOR_BRANCHES.map((b) => (
          <button key={b} style={tabBtn(branch === b)} onClick={() => setBranch(b)}>{b}</button>
        ))}
      </div>

      {/* Week navigator — ‹ this week · next week › */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
        <button
          onClick={() => setWeek("this")}
          disabled={week === "this"}
          aria-label="This week"
          style={{
            width: 30, height: 30, borderRadius: 8, border: "1px solid var(--ct-line)",
            background: "var(--ct-surface)", cursor: week === "this" ? "default" : "pointer",
            fontSize: 16, color: "var(--ct-ink-2)", opacity: week === "this" ? 0.4 : 1,
            display: "grid", placeItems: "center", fontFamily: "inherit",
          }}
        >
          ‹
        </button>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ct-ink)", minWidth: 84, textAlign: "center" }}>
          {week === "next" ? "Next week" : "This week"}
        </div>
        <button
          onClick={() => setWeek("next")}
          disabled={week === "next"}
          aria-label="Next week"
          style={{
            width: 30, height: 30, borderRadius: 8, border: "1px solid var(--ct-line)",
            background: "var(--ct-surface)", cursor: week === "next" ? "default" : "pointer",
            fontSize: 16, color: "var(--ct-ink-2)", opacity: week === "next" ? 0.4 : 1,
            display: "grid", placeItems: "center", fontFamily: "inherit",
          }}
        >
          ›
        </button>
      </div>

      {/* Status badge */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ct-ink)" }}>{branch}</span>
        <span style={{ fontSize: 11.5, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: statusStyle.bg, color: statusStyle.fg }}>
          {statusText}
        </span>
      </div>

      {loading && (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--ct-ink-3)", fontSize: 13 }}>Loading…</div>
      )}
      {err && !loading && (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--ct-crit)", fontSize: 13 }}>{err}</div>
      )}
      {!loading && !err && rows.length === 0 && (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--ct-ink-3)", fontSize: 13 }}>
          {week === "next"
            ? `${branch} hasn't submitted next week's roster yet.`
            : `No roster found for ${branch}.`}
        </div>
      )}

      {!loading && !err && rows.length > 0 && (
        <div style={{ border: "1px solid var(--ct-line)", borderRadius: 10, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: "inherit" }}>
            <thead>
              <tr style={{ background: "var(--ct-surface)" }}>
                <th style={{ textAlign: "left", padding: "10px 14px", fontWeight: 600, color: "var(--ct-ink-2)", borderBottom: "1px solid var(--ct-line)", width: 180, whiteSpace: "nowrap" }}>
                  Employee
                </th>
                {DAY_LABELS.map((d, i) => {
                  const iso = dayDates[WEEKDAY_FULL[i]] || "";
                  const dayNum = iso ? iso.slice(-2) : "";
                  return (
                    <th key={d} style={{ textAlign: "center", padding: "10px 6px", fontWeight: 600, color: "var(--ct-ink-2)", borderBottom: "1px solid var(--ct-line)", minWidth: 90 }}>
                      <div>{d}</div>
                      <div style={{ fontWeight: 400, fontSize: 11, color: "var(--ct-ink-3)" }}>{dayNum}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, rowIdx) => (
                <tr key={r.staffId || r.name} style={{ background: rowIdx % 2 === 0 ? "transparent" : "var(--ct-surface)" }}>
                  <td style={{ padding: "10px 14px", borderBottom: "1px solid var(--ct-line-soft, var(--ct-line))", whiteSpace: "nowrap", fontWeight: 500, color: "var(--ct-ink)" }}>
                    {r.name}
                  </td>
                  {DAY_LABELS.map((_, i) => {
                    const v = (r.days[WEEKDAY_FULL[i]] || "") as ShiftValue;
                    return (
                      <td key={i} style={{ padding: "10px 6px", textAlign: "center", borderBottom: "1px solid var(--ct-line-soft, var(--ct-line))", fontSize: 12, ...cellStyle(v) }}>
                        {v || "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Entry: managers edit their branch; operators monitor all branches ──
export function RosterMaker() {
  const userBranch = getUserBranch();
  if (userBranch === null) return <RosterMonitor />;
  return <RosterEditor branchId={userBranch} />;
}
